import React, {useEffect} from "react";
import {useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {Formik} from "formik";
import {AdminLayoutFullHeightAndTwoCells} from "../../atoms/admin-layout";
import {DocumentTitle} from "../../atoms/document-title";
import {
  Button,
  TextField,
  SelectField,
  FormSlide,
  FormCard,
  FormGrid,
  Text,
  TextAreaField,
} from "@reside/ui";
import {Spinner} from "../../atoms/spinner";
import {RoleNameEnum} from "../../utils/types/common";
import {CreateSsoConfigurationRequest} from "../../services/SsoService";
import {useCreateSsoConfiguration, useSsoConfigurationQuery} from "./hooks";
import {SSO_CONFIGURATION_VALIDATION_SCHEMA} from "./validator";
import {IDP_CLIENT_OPTIONS, IDP_CLIENT_FIELD_LABELS} from "./constants";
import {BackButton} from "./BackButton";
import {select} from "../../store";

const CREATE_SSO_CONFIGURATION_PAGE_TITLE =
  "Reside - Admin - Create SSO Configuration";

/**
 * Form to create new SSO configuration.
 * Prevents duplicates by redirecting if config exists.
 */
export const PageAdminSsoConfigurationCreate = () => {
  const navigate = useNavigate();
  const {organizationId} = useParams<{organizationId: string}>();
  const {data: existingConfig, isLoading: isCheckingExisting} =
    useSsoConfigurationQuery();
  const {mutate: createConfiguration, isLoading} = useCreateSsoConfiguration();

  const userRole = useSelector(select.adminSession.userRoleName);
  const isOrganizationItAdmin = userRole === RoleNameEnum.ORGANIZATION_IT_ADMIN;

  // Redirect if config exists to prevent duplicates
  useEffect(() => {
    if (!isCheckingExisting && existingConfig) {
      navigate(`/admin/organizations/${organizationId}/sso-configuration`);
    }
  }, [isCheckingExisting, existingConfig, navigate, organizationId]);

  const initialValues: CreateSsoConfigurationRequest = {
    idpClient: "",
    certificate: "",
    issuerUrl: "",
    samlEndpoint: "",
    sloEndpoint: "",
  };

  const handleSubmit = (values: CreateSsoConfigurationRequest) => {
    createConfiguration(values);
  };

  const renderPageContent = () => {
    if (isCheckingExisting) {
      return <Spinner />;
    }

    return (
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={values => SSO_CONFIGURATION_VALIDATION_SCHEMA(values)}
      >
        {({submitForm, values}) => {
          const fieldLabels =
            IDP_CLIENT_FIELD_LABELS[
              values.idpClient as keyof typeof IDP_CLIENT_FIELD_LABELS
            ] ?? IDP_CLIENT_FIELD_LABELS.onelogin;
          return (
            <FormGrid>
              <FormSlide>
                <FormCard title="SSO Configuration">
                  <Text as="p" fontSize={[3]} style={{marginBottom: "32px"}}>
                    Enter and submit the details below to configure SSO
                  </Text>
                  <SelectField
                    label="IDP CLIENT"
                    name="idpClient"
                    options={IDP_CLIENT_OPTIONS}
                    getOptionLabel={option => option.label}
                    getOptionValue={option => option.value}
                    required
                  />
                  <TextAreaField
                    label="x.509 certificate"
                    name="certificate"
                    required
                  />
                  <TextField
                    label={fieldLabels.issuerUrl}
                    name="issuerUrl"
                    required
                  />
                  <TextField
                    label={fieldLabels.samlEndpoint}
                    name="samlEndpoint"
                    required
                  />
                  <TextField
                    label={fieldLabels.sloEndpoint}
                    name="sloEndpoint"
                    required
                  />
                  <Button
                    onClick={submitForm}
                    color="primary"
                    style={{marginTop: "32px"}}
                    disabled={isLoading}
                  >
                    {isLoading ? "Submitting..." : "Submit"}
                  </Button>
                </FormCard>
              </FormSlide>
            </FormGrid>
          );
        }}
      </Formik>
    );
  };

  return (
    <AdminLayoutFullHeightAndTwoCells>
      <DocumentTitle title={CREATE_SSO_CONFIGURATION_PAGE_TITLE} />

      {!isOrganizationItAdmin && (
        <BackButton
          to={`/admin/organizations/${organizationId}/edit`}
          label="Edit Organization"
        />
      )}

      {renderPageContent()}
    </AdminLayoutFullHeightAndTwoCells>
  );
};
